import { Routing, UserConstants } from "@common/_constants/";
import { ErrorService } from "@common/_services";
import { refetch } from "@common/_services/refetch";
import { getTokenFromStorage } from "@common/_services/storage.service";
import { UserFromStorage } from "@common/models/UserFromStorage";
import { TelemetryService } from "@common/_services";
import * as HttpStatus from "http-status-codes";

interface AuthRequest {
    provider: string;
    code: string;
    state_code: string;
    redirect_url: string;
}

export const UserService = {
    activateUser,
    confirm,
    getAndRemoveRelodeRedirect,
    getActivities,
    getSignUpLookups,
    getUserFromStorage,
    getUserToActivate,
    isLoggedIn,
    isLoggedInAndAuthorized,
    saveContactSettings,
    saveRelodeRedirect,
    storeUserInStorage,
    updateProfileImage,
};

function saveContactSettings(settings) {
    const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json", credentials: "include" },
        body: JSON.stringify(settings),
    };
    const url = Routing.Api.General.Profile.SAVE_CONTACT_SETTINGS;
    return refetch(url, requestOptions)
        .then(resp => ErrorService.handleErrors(resp))
        .then(resp => resp.json());
}

function confirm(userId: number, code: string) {
    const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json", credentials: "include" },
        body: JSON.stringify({ user_id: userId, code }),
    };

    const url = Routing.Api.General.CONFIRMATION;

    return refetch(url, requestOptions, false)
        .then(resp => ErrorService.handleErrors(resp))
        .then(resp => resp.json())
        .then(apiResult => apiResult);
}

function getSignUpLookups(abortSignal: AbortSignal) {
    const requestOptions = {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        signal: abortSignal,
    };

    const url = Routing.Api.General.SIGN_UP_LOOKUPS;
    return refetch(url, requestOptions, false)
        .then(resp => ErrorService.handleErrors(resp))
        .then(resp => resp.json())
        .then(apiResult => {
            return apiResult;
        });
}

function isLoggedIn() {
    return !!UserService.getUserFromStorage();
}

function isLoggedInAndAuthorized() {
    if (!isLoggedIn()) {
        return Promise.resolve(false);
    }

    const urlRequiringAuth = Routing.Api.General.Profile.GET_CONTACT_SETTINGS;
    const options = {};
    const withAuth = true;
    const noRedirectOnError = false;
    return refetch(urlRequiringAuth, options, withAuth, noRedirectOnError).then(response => {
        return response.status === HttpStatus.OK;
    });
}

function getUserToActivate(code, key, abortSignal: AbortSignal) {
    const requestOptions = {
        method: "GET",
        headers: {
            "Content-Type": "application/json; charset=utf-8",
            credentials: "include",
        },
        signal: abortSignal,
    };

    const url = Routing.Api.General.ACTIVATE_USER + `?code=${code}&key=${key}`;
    return refetch(url, requestOptions, false)
        .then(resp => ErrorService.handleErrors(resp))
        .then(resp => resp.json())
        .then(json => json.result);
}

function activateUser(user) {
    const requestOptions = {
        method: "POST",
        headers: {
            "Content-Type": "application/json; charset=utf-8",
            credentials: "include",
        },
        body: JSON.stringify(user),
    };

    const url = Routing.Api.General.ACTIVATE_USER;
    return refetch(url, requestOptions, false)
        .then(resp => ErrorService.handleErrors(resp))
        .then(resp => resp.json())
        .then(json => json.result)
        .then(result => {
            storeUserInStorage(result);
            return result;
        });
}

function saveRelodeRedirect(redirect: string) {
    localStorage.setItem(Routing.Social.RELODE_REDIRECT_KEY, redirect);
}

function updateProfileImage(url: string) {
    const user = UserService.getUserFromStorage();
    if (user) {
        user.profileImageUrl = url;
        storeUserInStorage(user);
    }
}

function getAndRemoveRelodeRedirect(): string {
    let redirectUrl = localStorage.getItem(Routing.Social.RELODE_REDIRECT_KEY) as string;
    if (!!redirectUrl) {
        localStorage.removeItem(Routing.Social.RELODE_REDIRECT_KEY);
    } else {
        redirectUrl = Routing.Client.ROOT;
    }
    return redirectUrl;
}

export interface GetActivitiesOptions {
    current_page: number;
    page_size: number;
    range_end: string;
    range_start: string;
}

function getActivities(options: GetActivitiesOptions, abortSignal: AbortSignal) {
    const url = Routing.Api.General.Profile.PROFILE_ACTIVITIES;
    const settings = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(options),
        signal: abortSignal,
    };
    return refetch(url, settings)
        .then(resp => ErrorService.handleErrors(resp))
        .then(resp => resp.json())
        .then(json => json.result);
}

export function getUserFromStorage(): UserFromStorage | undefined {
    const user = getTokenFromStorage<UserFromStorage>(UserConstants.Storage.LOCAL_STORAGE_KEY);
    return user;
}

export const removeUserFromStorage = () => {
    TelemetryService.userLogout();
    localStorage.removeItem(UserConstants.Storage.LOCAL_STORAGE_KEY);
};

export function storeUserInStorage(user: any) {
    const _user: UserFromStorage = {
        token: user.token,
        firstName: user.firstName,
        displayName: user.displayName,
        email: user.email,
        roleId: user.roleId,
        profileImageUrl: user.profileImageUrl,
        expires: user.expires,
        applicationName: user.applicationName,
    };
    localStorage.setItem(UserConstants.Storage.LOCAL_STORAGE_KEY, JSON.stringify(_user));
}
